import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import { FeedArticle } from '@/views/Stream';
import {
  formatNetworth,
  getUserNameByUserInfo,
  login,
  openInDebank,
  openNewWindow,
  withHttp,
} from '@/utils';
import { useAuthDapp } from './hooks';
import { CommonModal } from '@/components/Modal/CommonModal';
import { useCurrentAddressInfo, useDeviceTypeIsMobile, useSwitch, useTheme } from '@/hooks';
import Button from '@/components/Button';
import { UserAvatar } from '@/components/UserAvatar';
import clsx from 'clsx';
import Tips from '@/components/Tips';

import { SCOPE_DICT } from '@/views/Connect/const';

export const AuthHeader = ({
  articleId,
  template,
  onSuccess,
}: {
  articleId?: number;
  template?: FeedArticle['template'];
  onSuccess: (accessToken: string) => void;
}) => {
  const { t } = useTranslation();

  const { mutateAsync, isLoading } = useAuthDapp();
  const { on, turnOff, turnOn } = useSwitch();
  const { isVerified } = useCurrentAddressInfo();
  const { isDark } = useTheme();
  const isMobile = useDeviceTypeIsMobile();

  const url = useMemo(() => withHttp(template?.app?.host || ''), [template?.app?.host]);

  const isAuthorized = useMemo(
    () => !!template?.user?.access_token,
    [template?.user?.access_token],
  );

  const handleClickLink = useCallback(() => {
    if (template?.app?.host) {
      openNewWindow(url);
    }
  }, [template?.app?.host, url]);

  const handleClickAuthBtn = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (isMobile) {
        openInDebank({ link: `/stream/${articleId}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }

      turnOn();
    },
    [articleId, isMobile, isVerified, turnOn],
  );

  const handleConfirm = useCallback(async () => {
    if (isLoading) return;
    const res = await mutateAsync({ templateId: template?.id! });
    if (res?.is_success) {
      turnOff();
      onSuccess(res?.access_token);
    }
  }, [isLoading, mutateAsync, onSuccess, template?.id, turnOff]);

  return (
    <>
      <div className={clsx(styles.header, isDark && styles.isDark)}>
        <div className={styles.title}>{t('ThirdPartyAppCard_AuthHeader.1a30gwx')}</div>
        <div className={styles.linkContainer}>
          <span className={styles.link} onClick={handleClickLink}>
            {template?.app?.host}
          </span>
        </div>
        {isAuthorized ? (
          <div className={styles.Authorized}>{t('ThirdPartyAppCard_AuthHeader.85dewr')}</div>
        ) : (
          <div className={styles.authBtn} onClick={handleClickAuthBtn}>
            {t('ThirdPartyAppCard_AuthHeader.1pckdhm')}
            <Tips className={styles.authTipIcon} title={t('ThirdPartyAppCard_AuthHeader.allow')} />
          </div>
        )}
      </div>
      {on ? (
        <AuthModal on={on} turnOff={turnOff} template={template} onConfirm={handleConfirm} />
      ) : null}
    </>
  );
};

const AuthModal = ({
  on,
  turnOff,
  template,
  onConfirm,
}: {
  on: boolean;
  turnOff: () => void;
  onConfirm: () => void;
  template?: FeedArticle['template'];
}) => {
  const { t } = useTranslation();

  const { userInfo: currentUserInfo } = useCurrentAddressInfo();

  const handleClick = useCallback(() => {
    if (template?.app?.host) {
      openNewWindow(withHttp(template?.app?.host));
    }
  }, [template?.app?.host]);

  const scopes = useMemo(
    () => template!.client?.scope.map(x => SCOPE_DICT[x]).filter(x => !!x),
    [template],
  );

  return (
    <CommonModal on={on} containerClassName={styles.authModal} hideCloseBtn turnOff={turnOff}>
      <div className={styles.title}>
        {t('ThirdPartyAppCard_AuthHeader.1ljng09')}
        <span onClick={handleClick}>{template?.app?.host}</span> ({template?.app?.name}
        {t('ThirdPartyAppCard_AuthHeader.wh3ii1')}
      </div>
      <div className={styles.subTitle}>{t('ThirdPartyAppCard_AuthHeader.1qctnzp')}</div>
      <div className={styles.userItem}>
        <UserAvatar data={currentUserInfo?.desc} size={36} />
        <div className={styles.userInfo}>
          <div className={styles.userId}>
            <div className={styles.userIdText}>{getUserNameByUserInfo(currentUserInfo)}</div>
            <span className={styles.userAsset}>
              {formatNetworth(currentUserInfo?.desc?.usd_value)}
            </span>
          </div>
          <div className={styles.address}>{currentUserInfo?.id}</div>
        </div>
      </div>
      {scopes.map((x, i) => (
        <div className={styles.tips} key={i}>
          <div className={styles.dotContainer}>
            <div className={styles.dot} />
          </div>
          <div className={styles.right}>{x}</div>
        </div>
      ))}
      <div className={styles.operates}>
        <Button className={styles.modalBtn} onClick={() => turnOff()}>
          {t('ThirdPartyAppCard_AuthHeader.x9d2fu')}
        </Button>
        <Button className={styles.modalBtn} onClick={() => onConfirm()} type="primary">
          {t('ThirdPartyAppCard_AuthHeader.179d1nk')}
        </Button>
      </div>
    </CommonModal>
  );
};
